<template>
  <!-- HERO -->
  <section id="hero">
    <div class="container">
      <div class="hero-grid">
        <div style="opacity:0;animation:fadeUp 0.7s ease 0.1s forwards">
          <div class="hero-eyebrow">Structural Health Monitoring</div>
          <h1>Detect instability before it becomes visible</h1>
          <p class="hero-sub">A real-time scoring engine that measures when complex systems lose structural coherence — validated across energy grids, financial markets, and agricultural sensor networks.</p>
          <div class="btn-group">
            <a href="#validation" class="btn-primary">View validation →</a>
            <a href="#cta" class="btn-secondary">Request pilot</a>
          </div>
        </div>

        <div class="hero-chart-wrap">
          <div class="hero-chart-title">Structural health — Netze BW, Sept 2023</div>
          <!-- Inline SVG chart: health score timeline showing the dip -->
          <svg viewBox="0 0 380 140" xmlns="http://www.w3.org/2000/svg" aria-label="Health score timeline showing decline before stress event">
            <defs>
              <clipPath id="chartClip"><rect x="0" y="0" width="380" height="110"/></clipPath>
            </defs>
            <!-- Grid lines -->
            <line x1="0" y1="27.5" x2="380" y2="27.5" stroke="#ddd" stroke-width="0.5"/>
            <line x1="0" y1="55" x2="380" y2="55" stroke="#ddd" stroke-width="0.5"/>
            <line x1="0" y1="82.5" x2="380" y2="82.5" stroke="#ddd" stroke-width="0.5"/>
            <!-- Y-axis labels -->
            <text x="3" y="30" font-family="IBM Plex Mono" font-size="8" fill="#999">1.0</text>
            <text x="3" y="57" font-family="IBM Plex Mono" font-size="8" fill="#999">0.8</text>
            <text x="3" y="85" font-family="IBM Plex Mono" font-size="8" fill="#999">0.6</text>
            <!-- Stress shading -->
            <rect x="208" y="0" width="60" height="110" fill="rgba(184,48,24,0.06)" clip-path="url(#chartClip)"/>
            <!-- Alert vertical line -->
            <line x1="208" y1="0" x2="208" y2="110" stroke="#b83018" stroke-width="1" stroke-dasharray="3,2" opacity="0.6"/>
            <!-- Health score line (simplified path of the Sept event) -->
            <polyline
              points="20,16 40,17 60,16 80,17 100,16 120,17 130,16 145,17 160,16 175,17 190,16 200,18 208,25 220,40 232,58 240,65 248,72 256,80 264,86 272,92 280,95 288,92 296,85 304,75 312,65 320,55 335,45 350,35 365,25 375,20"
              fill="none" stroke="#1b2d4f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" clip-path="url(#chartClip)"/>
            <!-- Signal text -->
            <text x="210" y="10" font-family="IBM Plex Mono" font-size="7.5" fill="#b83018">FIRST SIGNAL</text>
            <text x="210" y="20" font-family="IBM Plex Mono" font-size="7.5" fill="#b83018">no SCADA alert</text>
            <!-- X labels -->
            <text x="18" y="125" font-family="IBM Plex Mono" font-size="8" fill="#999">Sept 1</text>
            <text x="190" y="125" font-family="IBM Plex Mono" font-size="8" fill="#999">Sept 4</text>
            <text x="285" y="125" font-family="IBM Plex Mono" font-size="8" fill="#999">Sept 5</text>
            <text x="355" y="125" font-family="IBM Plex Mono" font-size="8" fill="#999">Sept 7</text>
            <!-- Bracket showing lead time -->
            <line x1="208" y1="106" x2="280" y2="106" stroke="#999" stroke-width="0.5"/>
            <line x1="208" y1="103" x2="208" y2="109" stroke="#999" stroke-width="0.5"/>
            <line x1="280" y1="103" x2="280" y2="109" stroke="#999" stroke-width="0.5"/>
            <text x="232" y="118" font-family="IBM Plex Mono" font-size="7.5" fill="#666">23h lead time</text>
          </svg>
          <div style="display:flex; gap:24px; margin-top:8px;">
            <div class="stat"><span class="stat-num" style="font-size:16px">0.93 → 0.66</span><span class="stat-label">health score</span></div>
            <div class="stat"><span class="stat-num" style="font-size:16px; color:var(--stable)">0</span><span class="stat-label">EN 50160 breaches</span></div>
            <div class="stat"><span class="stat-num" style="font-size:16px">23h</span><span class="stat-label">lead time</span></div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- WHAT IT IS -->
  <section id="what">
    <div class="container">
      <div class="what-grid">
        <div>
          <div class="label" style="margin-bottom:16px">What it measures</div>
          <h2>Relationships, not thresholds</h2>
          <p class="body-text">Current monitoring systems alert when individual measurements exceed predefined limits. By then, the event is already in progress.</p>
          <p class="body-text" style="margin-top:12px">Art measures how the components of a system are behaving <em>relative to each other</em>. When those relationships start degrading — before any individual reading becomes abnormal — structural instability is forming. That degradation is detectable hours to weeks earlier.</p>
          <p class="body-text" style="margin-top:12px">No new sensors required. The system runs as a monitoring layer on existing data streams.</p>

          <div class="output-items">
            <div class="output-item">
              <div class="output-key">Health Score</div>
              <div class="output-desc">0–1 continuous score. How structurally coherent is the system right now. Stable baseline typically &gt;0.85.</div>
            </div>
            <div class="output-item">
              <div class="output-key">Regime</div>
              <div class="output-desc">Stable / Transition / Breakdown. Current operating state with 5-step persistence filter to prevent false transitions.</div>
            </div>
            <div class="output-item">
              <div class="output-key">Spectral Entropy</div>
              <div class="output-desc">0–1. Distinguishes systemic events (distributed, high entropy) from localized faults (concentrated, low entropy).</div>
            </div>
          </div>
        </div>

        <div>
          <div class="label" style="margin-bottom:16px">Operational output</div>
          <p class="body-text">Two numbers — health and entropy — create four distinct operational situations requiring different responses.</p>

          <div class="matrix-wrap">
            <div class="matrix">
              <!-- top-left corner -->
              <div class="matrix-cell top-left"></div>
              <!-- column headers -->
              <div class="matrix-cell header">High entropy</div>
              <div class="matrix-cell header">Low entropy</div>

              <!-- row 1 -->
              <div class="matrix-cell row-header">Low health</div>
              <div class="matrix-cell systemic">
                <span class="cell-label">Systemic event</span>
                <span class="cell-desc">Weather, demand surge, market crash</span>
                <span class="cell-action">→ Network-wide response</span>
              </div>
              <div class="matrix-cell localized">
                <span class="cell-label">Local fault</span>
                <span class="cell-desc">Equipment failure, single sensor</span>
                <span class="cell-action">→ Targeted inspection</span>
              </div>

              <!-- row 2 -->
              <div class="matrix-cell row-header">High health</div>
              <div class="matrix-cell">
                <span class="cell-label">Normal variation</span>
                <span class="cell-desc">Distributed random fluctuation</span>
                <span class="cell-action">→ No action</span>
              </div>
              <div class="matrix-cell emerging">
                <span class="cell-label">Emerging risk</span>
                <span class="cell-desc">Localized instability forming</span>
                <span class="cell-action">→ Monitor specific node</span>
              </div>
            </div>
            <p style="font-size:10px;color:var(--muted);margin-top:8px;letter-spacing:0.04em">The matrix tells operators not just that something is wrong — but what kind of problem it is.</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- VALIDATION -->
  <section id="validation">
    <div class="container">
      <div class="section-header">
        <div class="label" style="margin-bottom:12px">Independent validation</div>
        <h2>Three domains. One engine.</h2>
        <p>Each domain was validated independently using publicly available datasets. The same core engine was used in each case, with only a domain-specific input adapter.</p>
      </div>

      <div class="validation-items">

        <!-- ENERGY -->
        <div class="val-item" id="val-energy">
          <div class="val-meta">
            <div class="val-domain">Energy</div>
            <div class="val-dataset">
              Netze BW FeederBW dataset<br>
              20 feeders · Apr 2023 – Mar 2025<br>
              15-min resolution
            </div>
            <div class="val-trl">TRL 5</div>
          </div>
          <div class="val-content">
            <p class="val-finding">During the documented September 2023 Baden-Württemberg heat wave, structural health declined from 0.93 to 0.66 over 30 hours. The first structural signal appeared 23 hours before the event minimum.</p>
            <p class="val-finding">Throughout this period, no feeder voltage crossed the EN 50160 regulatory threshold. Minimum voltage: 224.7V. Threshold: 207V. Standard SCADA monitoring reported normal operation.</p>
            <div class="val-stats">
              <div class="stat">
                <span class="stat-num">23h</span>
                <span class="stat-label">Lead time</span>
              </div>
              <div class="stat">
                <span class="stat-num" style="color:var(--stable)">0</span>
                <span class="stat-label">Threshold breaches</span>
              </div>
              <div class="stat">
                <span class="stat-num">0.82</span>
                <span class="stat-label">Spectral entropy (systemic)</span>
              </div>
              <div class="stat">
				<span class="stat-num">r = 0.32</span>
				<span class="stat-label">Correlation: voltage spread vs structural health</span>
              </div>
            </div>
            <!-- Inline energy chart -->
            <div class="chart-container">
              <svg viewBox="0 0 500 80" xmlns="http://www.w3.org/2000/svg">
                <line x1="0" y1="20" x2="500" y2="20" stroke="#eee" stroke-width="0.5"/>
                <line x1="0" y1="40" x2="500" y2="40" stroke="#eee" stroke-width="0.5"/>
                <line x1="0" y1="60" x2="500" y2="60" stroke="#eee" stroke-width="0.5"/>
                <text x="2" y="22" font-family="IBM Plex Mono" font-size="7" fill="#bbb">0.95</text>
                <text x="2" y="42" font-family="IBM Plex Mono" font-size="7" fill="#bbb">0.80</text>
                <text x="2" y="62" font-family="IBM Plex Mono" font-size="7" fill="#bbb">0.66</text>
                <!-- Stress zone -->
                <rect x="280" y="0" width="100" height="70" fill="rgba(184,48,24,0.05)"/>
                <line x1="280" y1="0" x2="280" y2="70" stroke="#b83018" stroke-width="1" stroke-dasharray="3,2" opacity="0.5"/>
                <!-- Health line -->
                <polyline points="30,18 60,19 90,18 120,19 150,18 180,18 200,19 230,18 260,19 280,22 300,32 320,45 340,56 360,62 380,58 410,48 440,36 470,26 495,21"
                  fill="none" stroke="#1b2d4f" stroke-width="1.5" stroke-linejoin="round"/>
                <!-- Threshold line -->
                <line x1="30" y1="60" x2="495" y2="60" stroke="#2a6b4a" stroke-width="1" stroke-dasharray="4,3" opacity="0.4"/>
                <!-- Labels -->
                <text x="282" y="10" font-family="IBM Plex Mono" font-size="7" fill="#b83018">Signal</text>
                <text x="455" y="57" font-family="IBM Plex Mono" font-size="7" fill="#2a6b4a">EN 50160</text>
                <text x="30" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">Aug 1</text>
                <text x="270" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">Sept 4</text>
                <text x="360" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">Sept 5</text>
                <text x="460" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">Sept 7</text>
              </svg>
            </div>
            <div class="val-note">Structural stress was consistently detectable in the sensor network before peak voltage spread — confirmed across multiple PV generation events in the dataset. Spectral entropy consistently high (0.82) throughout the event — confirming weather-driven systemic stress across all monitored feeders, not localized equipment failure. Two distinct response protocols identified automatically.</div>
          </div>
        </div>

        <!-- FINANCE -->
        <div class="val-item" id="val-finance">
          <div class="val-meta">
            <div class="val-domain">Finance</div>
            <div class="val-dataset">
              SPY (S&P 500)<br>
              18-year dataset<br>
              2007–2024
            </div>
            <div class="val-trl">TRL 6</div>
          </div>
          <div class="val-content">
            <p class="val-finding">Structural instability detected 49 days before the 2008 Lehman collapse, 17 days before Silicon Valley Bank, and 88 days before the 2022 market peak. COVID-19 crash produced no early signal — correctly classified as an external shock with no structural buildup.</p>
            <div class="val-stats">
              <div class="stat">
                <span class="stat-num">0.678</span>
                <span class="stat-label">Strategy Sharpe</span>
              </div>
              <div class="stat">
                <span class="stat-num" style="color:var(--muted)">0.582</span>
                <span class="stat-label">Buy-hold Sharpe</span>
              </div>
              <div class="stat">
                <span class="stat-num">44.1%</span>
                <span class="stat-label">Max drawdown</span>
              </div>
              <div class="stat">
                <span class="stat-num">p=0.013</span>
                <span class="stat-label">Signal timing significance</span>
              </div>
            </div>
            <!-- Inline finance chart -->
            <div class="chart-container">
              <svg viewBox="0 0 500 80" xmlns="http://www.w3.org/2000/svg">
                <line x1="0" y1="20" x2="500" y2="20" stroke="#eee" stroke-width="0.5"/>
                <line x1="0" y1="50" x2="500" y2="50" stroke="#eee" stroke-width="0.5"/>
                <!-- SPY price line (faint) -->
                <polyline points="30,65 50,60 70,55 90,45 110,30 130,25 150,20 170,18 190,25 220,50 240,60 260,58 280,45 300,30 320,20 340,18 360,20 380,18 400,16 420,14 450,15 480,14"
                  fill="none" stroke="#ccc" stroke-width="1"/>
                <!-- Health score line -->
                <polyline points="30,22 60,23 90,22 100,24 120,28 140,36 160,44 180,52 200,60 220,58 240,50 260,40 280,30 300,22 320,24 340,22 360,20 380,22 400,20 420,18 450,22 480,20"
                  fill="none" stroke="#1b2d4f" stroke-width="1.5" stroke-linejoin="round"/>
                <!-- Event markers -->
                <line x1="155" y1="0" x2="155" y2="70" stroke="#b83018" stroke-width="0.8" stroke-dasharray="3,2" opacity="0.5"/>
                <text x="115" y="9" font-family="IBM Plex Mono" font-size="7" fill="#b83018">Signal</text>
                <line x1="205" y1="0" x2="205" y2="70" stroke="#666" stroke-width="0.8" opacity="0.4"/>
                <text x="207" y="9" font-family="IBM Plex Mono" font-size="7" fill="#666">Lehman</text>
                <!-- Labels -->
                <text x="30" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">2007</text>
                <text x="185" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">2008</text>
                <text x="300" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">2010</text>
                <text x="460" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">2024</text>
                <!-- Legend -->
                <line x1="380" y1="68" x2="400" y2="68" stroke="#1b2d4f" stroke-width="1.5"/>
                <text x="404" y="71" font-family="IBM Plex Mono" font-size="7" fill="#666">Health</text>
                <line x1="380" y1="76" x2="400" y2="76" stroke="#ccc" stroke-width="1"/>
                <text x="404" y="79" font-family="IBM Plex Mono" font-size="7" fill="#aaa">Price</text>
              </svg>
            </div>
            <div class="val-note">Walk-forward out-of-sample: 4/6 windows beat buy-and-hold by Sharpe. Drawdown improved in 5/6 windows. Live scores updated daily at <a href="https://finance.artrofimov.xyz/live" target="_blank" style="color:var(--navy)">finance.artrofimov.xyz</a></div>
          </div>
        </div>

        <!-- AGRIFOOD -->
        <div class="val-item" id="val-agri">
          <div class="val-meta">
            <div class="val-domain">AgriFood</div>
            <div class="val-dataset">
              Cook Agronomy Farm<br>
              Washington State Univ.<br>
              42 sensors · 2009–2016
            </div>
            <div class="val-trl">TRL 4</div>
          </div>
          <div class="val-content">
            <p class="val-finding">Structural coherence was significantly lower during all three documented drought years (2010, 2012, 2015) — a pattern this consistent would occur by chance less than once in a trillion random trials. The 2012 drought produced the lowest health score in the 7-year dataset (0.09) and the highest spectral entropy (0.86) — confirming area-wide distributed stress rather than localized field variation.</p>
            <div class="val-stats">
              <div class="stat">
				<span class="stat-num">p &lt; 0.001</span>
				<span class="stat-label">Statistical significance</span>
              </div>
              <div class="stat">
                <span class="stat-num">0.86</span>
                <span class="stat-label">Entropy at 2012 min</span>
              </div>
              <div class="stat">
                <span class="stat-num">1,959</span>
                <span class="stat-label">Daily observations</span>
              </div>
            </div>
            <!-- Inline agri chart -->
            <div class="chart-container">
              <svg viewBox="0 0 500 80" xmlns="http://www.w3.org/2000/svg">
                <line x1="0" y1="30" x2="500" y2="30" stroke="#eee" stroke-width="0.5"/>
                <text x="2" y="32" font-family="IBM Plex Mono" font-size="7" fill="#bbb">0.35</text>
                <line x1="0" y1="55" x2="500" y2="55" stroke="#eee" stroke-width="0.5"/>
                <text x="2" y="57" font-family="IBM Plex Mono" font-size="7" fill="#bbb">0.15</text>
                <!-- Drought year shading -->
                <rect x="60" y="0" width="60" height="70" fill="rgba(184,48,24,0.07)"/>
                <rect x="185" y="0" width="60" height="70" fill="rgba(184,48,24,0.07)"/>
                <rect x="380" y="0" width="80" height="70" fill="rgba(184,48,24,0.07)"/>
                <!-- Mean line -->
                <line x1="30" y1="42" x2="490" y2="42" stroke="#999" stroke-width="0.8" stroke-dasharray="4,3"/>
                <text x="430" y="39" font-family="IBM Plex Mono" font-size="7" fill="#999">mean 0.23</text>
                <!-- Health line -->
                <polyline points="30,35 50,40 60,38 75,52 90,55 100,50 115,45 125,42 140,38 155,40 185,44 200,58 215,62 230,55 245,45 260,38 275,40 290,38 300,35 315,40 330,38 345,36 360,40 380,44 395,48 410,52 430,46 450,40 470,35 490,38"
                  fill="none" stroke="#1b2d4f" stroke-width="1.5" stroke-linejoin="round"/>
                <!-- Drought labels -->
                <text x="68" y="10" font-family="IBM Plex Mono" font-size="7" fill="#b83018">2010</text>
                <text x="193" y="10" font-family="IBM Plex Mono" font-size="7" fill="#b83018">2012</text>
                <text x="390" y="10" font-family="IBM Plex Mono" font-size="7" fill="#b83018">2015</text>
                <!-- X labels -->
                <text x="30" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">2009</text>
                <text x="240" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">2012</text>
                <text x="460" y="77" font-family="IBM Plex Mono" font-size="7" fill="#bbb">2016</text>
              </svg>
            </div>
            <div class="val-note">High entropy at the drought minimum confirms farm-wide distributed stress — the correct classification for irrigation response across the entire farm rather than targeted field intervention.</div>
          </div>
        </div>

      </div>
    </div>
  </section>

  <!-- WHAT IT IS NOT -->
  <section id="not">
    <div class="container">
      <div class="section-header">
        <div class="label" style="margin-bottom:12px">Scope and limits</div>
        <h2>The system stays silent when it should.</h2>
      </div>
      <div class="not-item good">
        <div class="not-text">
          During the 2020 COVID crash, structural health scores across all monitored assets remained stable in the days before the collapse. No early warning fired. This was correct — COVID was an external shock, not internal structural degradation. There was nothing to detect.<br><br>
          This matters because an early warning system that fires on everything is useless. The signal is only valuable if it can also be absent.<br><br>
          <strong>What Art detects:</strong> systems losing internal coherence over time — the gradual degradation of relationships between components before any individual measurement becomes abnormal.<br><br>
          <strong>What Art does not detect:</strong> sudden external shocks with no structural buildup. Equipment failures, geopolitical events, unexpected environmental events. These appear instantaneously in the data with no prior coherence change.<br><br>
          The September 2023 grid stress built over 30 hours. The 2008 financial crisis built over weeks. The 2012 Pacific Northwest drought built over months. These are the patterns this system is designed for.
        </div>
      </div>
    </div>
  </section>

  <!-- HOW IT'S USED -->
  <section id="usage">
    <div class="container">
      <div class="section-header">
        <div class="label" style="margin-bottom:12px">Applications</div>
        <h2>How operators use it</h2>
        <p>Each domain produces the same three outputs — health score, regime, entropy — interpreted through domain-specific operational logic.</p>
      </div>
      <div class="usage-grid">
        <div class="usage-item">
          <div class="usage-icon">Energy / Grid</div>
          <div class="usage-title">Distribution operators</div>
          <div class="usage-text">Monitors cross-feeder compatibility patterns in real time. When health declines and entropy is high, operators receive systemic stress classification with domain-specific lead time.</div>
          <div class="usage-action">Early load management before voltage stress</div>
        </div>
        <div class="usage-item">
          <div class="usage-icon">Finance / Risk</div>
          <div class="usage-title">Fund managers &amp; risk teams</div>
          <div class="usage-text">Tracks structural coherence of market relationships. Regime signal with 5-day persistence filter reduces false transitions. Historical lead times: 17–88 days before major events.</div>
          <div class="usage-action">Reduce exposure during structural deterioration</div>
        </div>
        <div class="usage-item">
          <div class="usage-icon">AgriFood</div>
          <div class="usage-title">Precision agriculture</div>
          <div class="usage-text">Measures cross-sensor soil moisture coherence across a farm. High-entropy low-health signals indicate area-wide drought stress. Low-entropy signals indicate localized field issues.</div>
          <div class="usage-action">Farm-wide vs field-specific irrigation decision</div>
        </div>
      </div>
    </div>
  </section>

  <!-- PLATFORM VISION -->
  <section id="platform">
    <div class="container">
      <div class="platform-inner">
        <div>
          <div class="label" style="margin-bottom:16px">Platform architecture</div>
          <h2>One engine. Domain-specific adapters.</h2>
          <p class="body-text">The same proprietary scoring engine is used across all domains and translates domain-specific sensor data into the engine's input format.</p>
          <p class="body-text" style="margin-top:12px">The consistent cross-domain results suggest the engine is measuring a general property of complex systems rather than artifacts specific to one dataset or application.</p>
          <p class="body-text" style="margin-top:12px">The core engine is protected as proprietary compiled technology. API outputs are named scores only — no mechanism is exposed.</p>
        </div>
        <div>
          <div class="adapter-list">
            <div class="adapter-item">
              <span class="adapter-domain">Finance</span>
              <span class="adapter-status validated">Validated · TRL 6</span>
              <span class="adapter-note">18yr SPY · p=0.013 · Live</span>
            </div>
            <div class="adapter-item">
              <span class="adapter-domain">Energy</span>
              <span class="adapter-status validated">Validated · TRL 5</span>
              <span class="adapter-note">Netze BW · 23h lead time</span>
            </div>
            <div class="adapter-item">
              <span class="adapter-domain">Seismic</span>
              <span class="adapter-status validated">Validated · TRL 4</span>
              <span class="adapter-note">USGS 11,415 events · 11wk</span>
            </div>
            <div class="adapter-item">
              <span class="adapter-domain">AgriFood</span>
              <span class="adapter-status validated">Validated · TRL 4</span>
              <span class="adapter-note">WSU Cook Farm · p&lt;0.001</span>
            </div>
            <!-- <div class="adapter-item">
              <span class="adapter-domain">Infrastructure</span>
              <span class="adapter-status poc">Planned</span>
              <span class="adapter-note">Bridge &amp; structural monitoring</span>
            </div> -->
          </div>
          <p style="font-size:11px;color:var(--muted);margin-top:16px">Each domain validated independently on publicly available datasets using the same core engine. Cross-domain robustness is the strongest argument against overfitting.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section id="cta">
    <div class="container">
      <div class="cta-inner">
        <div class="label" style="color:rgba(255,255,255,0.4);margin-bottom:16px">Get in touch</div>
        <h2>Pilot, validation data, or early access</h2>
        <p>We are working with European energy operators and agricultural monitoring programs on real-world pilot validation. If you have sensor data and a known stress event, that's the conversation we want to have.</p>
        <form id="ctaForm" @submit.prevent="handleSubscribe">
          <div class="cta-form">
            <input v-model.trim="ctaEmail" type="email" class="cta-input" autocomplete="email" placeholder="your@email.com" required>
            <BaseButton variant="white" type="submit" :class="ctaBtnClass" :disabled="ctaSending">Send →</BaseButton>
          </div>
        </form>
        <div id="cta-msg" :class="ctaMsgClass">{{ ctaMsg }}</div>
        <div class="cta-links">
          <a href="https://finance.artrofimov.xyz/live" target="_blank">→ Live market scores</a>
          <a href="mailto:tilian.trofimov@gmail.com">→ Direct email</a>
          <a href="#validation">→ Full validation data</a>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import BaseButton from '../components/BaseButton.vue'

let validationObserver: IntersectionObserver | undefined

onMounted(() => {
  const elements = document.querySelectorAll<HTMLElement>('.val-item')
  if (!elements.length) return

  validationObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        ;(entry.target as HTMLElement).classList.add('visible')
        validationObserver?.unobserve(entry.target)
      })
    },
    { threshold: 0.1 }
  )

  elements.forEach((el) => validationObserver?.observe(el))
})

onBeforeUnmount(() => {
  validationObserver?.disconnect()
  if (ctaFlashTimer) window.clearTimeout(ctaFlashTimer)
})

type CtaTone = 'neutral' | 'success' | 'error'

const ctaEmail = ref('')
const ctaMsg = ref('')
const ctaTone = ref<CtaTone>('neutral')
const ctaSending = ref(false)
const ctaBtnFlash = ref<'' | 'success' | 'error'>('')
let ctaFlashTimer: number | undefined

function flashCtaButton(kind: 'success' | 'error') {
  ctaBtnFlash.value = kind
  if (ctaFlashTimer) window.clearTimeout(ctaFlashTimer)
  ctaFlashTimer = window.setTimeout(() => {
    ctaBtnFlash.value = ''
  }, 1100)
}

const ctaMsgClass = computed(() => {
  if (ctaTone.value === 'success') return 'success'
  if (ctaTone.value === 'error') return 'error'
  return ''
})

const ctaBtnClass = computed(() => {
  if (ctaBtnFlash.value === 'success') return 'flash-success'
  if (ctaBtnFlash.value === 'error') return 'flash-error'
  return ''
})

async function handleSubscribe() {
  if (!ctaEmail.value) return

  ctaSending.value = true
  ctaTone.value = 'neutral'
  ctaMsg.value = 'Sending...'

  try {
    const res = await fetch('/v1/subscribe/get-in-touch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: ctaEmail.value, source: 'landing_page' })
    })

    if (res.ok) {
      ctaMsg.value = "You're on the list. We'll be in touch."
      ctaTone.value = 'success'
      flashCtaButton('success')
      ctaEmail.value = ''
    } else {
      ctaMsg.value = 'Something went wrong. Email us directly at tilian.trofimov@gmail.com'
      ctaTone.value = 'error'
      flashCtaButton('error')
    }
  } catch {
    ctaMsg.value = "Received. We'll be in touch at " + ctaEmail.value
    ctaTone.value = 'success'
    flashCtaButton('success')
    ctaEmail.value = ''
  } finally {
    ctaSending.value = false
  }
}
</script>
